import { StyledImageContainer, StyledLoginPage } from '../style';
import { LoginTermsBlock } from '../TermsBlocks/TermsBlock';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ELoginMode } from 'constants/enums';
import { WebviewEscapeScreen } from '../WebviewEscapeScreen/WebviewEscapeScreen';
import { LoginButtonBlocks } from './ButtonBlocks';
import { useLoginPage } from '../useLoginPage.hook';
import { useLoginState } from 'state/hooks/login.state.hook';
import { ConsentAlert } from '../TermsBlocks/ConsentAlert';
import { useAppState } from 'state/hooks/app.state.hook';
import { useStoreSettings } from '../../../hooks/useStoreSettings';

export const Login: React.FC = () => {
  const { publisherMetaData, isWebview, showOtpButton } = useLoginPage();
  const { handleBackBtnClicked, mode } = useLoginState();
  const { t } = useTranslation();
  const { setBackgroundProps } = useAppState();
  const [dynamicLogo, setDynamicLogo] = useState<string | null>(null);

  useEffect(() => {
    if (publisherMetaData.supportConfiguration?.postLoginSnippet) {
      (window as any).Helpshift && (window as any).Helpshift('hide');
    }
  }, []);

  // Listen for UPDATE_PREVIEW_VALUES messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'UPDATE_PREVIEW_VALUES' && event.data?.data?.logo) {
        setDynamicLogo(event.data.data.logo);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const {
    loginLogoSize,
    textSize = 24,
    textColor = 'blue',
    text = 'Welcome to appcharge store!',
    textWeight = 'normal',
  } = publisherMetaData.storeTheme.login;
  const logo =
    dynamicLogo ||
    publisherMetaData.storeTheme.login?.loginLogoImage ||
    publisherMetaData.storeTheme.general?.logo ||
    'https://media.appcharge.com/defaults/default-store-logo.svg';
  const fontFamily =
    publisherMetaData.storeTheme.login.font || publisherMetaData.storeTheme.general.font;
  const { footer: footerSettings } = useStoreSettings();

  const displayTermsBlock = !isWebview || showOtpButton;

  const displayWebviewEscapeScreen = isWebview && mode === ELoginMode.PROVIDERS && !showOtpButton;

  useEffect(() => {
    if (!publisherMetaData?.storeTheme) return;

    setBackgroundProps({
      height: '',
      minHeight: '100dvh',
      width: '100vw',
      backgroundImageMobile:
        publisherMetaData.storeTheme.login.loginBackgroundImageMobile ||
        publisherMetaData.storeTheme.general.backgroundImageMobile,
      backgroundImageDesktop:
        publisherMetaData.storeTheme.login.loginBackgroundImageDesktop ||
        publisherMetaData.storeTheme.general.backgroundImageDesktop,
      backgroundPosition: 'center top',
      backgroundSize: 'cover',
    });
  }, [publisherMetaData?.storeTheme]);

  return (
    <StyledLoginPage
      fontFamily={fontFamily}
      textColor={textColor}
      textSize={textSize}
      textWeight={textWeight}
    >
      <ConsentAlert />
      <StyledImageContainer>
        <img
          className="logo"
          id={loginLogoSize}
          alt="game logo"
          src={logo}
          style={{
            width: window.location.hostname.includes('matchmaste.rs') ? '230px' : 'auto',
          }}
        />
      </StyledImageContainer>
      {displayWebviewEscapeScreen ? (
        <>
          <WebviewEscapeScreen />
          {showOtpButton && <div className="orText">{t('login.or')}</div>}
        </>
      ) : (
        <h2 data-testid="loginPageTitle">{text}</h2>
      )}
      <LoginButtonBlocks />
      {displayTermsBlock && (
        <LoginTermsBlock
          textColor={textColor}
          privacyPolicyLink={footerSettings.privacyPolicyUrl}
          termsAndConditionsLink={footerSettings.termsAndConditionsUrl}
        />
      )}
      {mode !== ELoginMode.PROVIDERS && (
        <button
          className="backButton"
          style={{ color: textColor }}
          onClick={() => handleBackBtnClicked()}
        >
          {t('login.back')}
        </button>
      )}
    </StyledLoginPage>
  );
};
